import { USER_ROLES, type UserRole } from './company.js';

/**
 * As áreas que o Rimati atende por papel, na conversa de gestão.
 *
 * O servidor decide cada ferramenta pela permissão do módulo (a mesma que a
 * tela exige); esta lista é a vitrine disso para a interface — o que mostrar
 * como sugestão a quem abre a conversa. `owner` e `admin` recebem todas as
 * áreas e a conversa completa; os demais papéis recebem somente as áreas em
 * que `roles` os inclui.
 *
 * O teste `rimati-role-tools.test.ts` compara esta tabela com as ferramentas
 * que o servidor realmente oferece a cada papel. Se uma ferramenta nova mudar
 * o alcance de um papel, o teste falha até esta lista acompanhar.
 */
export interface RimatiChatArea {
  id: string;
  label: string;
  /** Papéis além de owner/admin que têm ao menos uma ferramenta nesta área. */
  roles: readonly UserRole[];
  examples: readonly string[];
}

const EVERYONE = USER_ROLES.filter((role) => role !== 'owner' && role !== 'admin');
const FLOOR = ['agent', 'manager', 'inventory', 'sales', 'sales_inventory', 'technician', 'delivery'] as const;

export const RIMATI_CHAT_AREAS: readonly RimatiChatArea[] = [
  {
    id: 'conhecimento-equipe',
    label: 'Informações da equipe',
    roles: EVERYONE,
    examples: ['Qual é o procedimento de abertura da loja?'],
  },
  {
    id: 'projetos',
    label: 'Projetos',
    roles: EVERYONE,
    examples: ['Quais projetos estão em andamento?'],
  },
  {
    id: 'documentos',
    label: 'Documentos',
    roles: EVERYONE,
    examples: ['Quais documentos vencem neste mês?'],
  },
  {
    id: 'rotinas',
    label: 'Rotinas e conferências',
    roles: FLOOR,
    examples: ['O que ainda falta nas rotinas de hoje?'],
  },
  {
    id: 'estoque-equipe',
    label: 'Contagens e perdas de estoque',
    roles: ['manager', 'inventory', 'sales_inventory'],
    examples: ['Há contagens de estoque aguardando revisão?'],
  },
  {
    id: 'estoque-operacao',
    label: 'Transferências de estoque',
    roles: ['sales_inventory'],
    examples: ['Mostre as transferências entre locais que ainda não foram recebidas.'],
  },
  {
    id: 'locacao',
    label: 'Locação: consultas, reserva, retirada e devolução',
    roles: ['manager', 'sales', 'sales_inventory', 'inventory', 'delivery'],
    examples: ['Quanto fica alugar o item por três dias?'],
  },
  {
    id: 'pos-venda',
    label: 'Qualidade e pós-venda',
    roles: ['inventory'],
    examples: ['Quais produtos tiveram mais devoluções neste mês?'],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    roles: ['finance'],
    examples: ['Como estão as entradas e saídas deste mês?'],
  },
  {
    id: 'orcamento',
    label: 'Orçamento gerencial',
    roles: ['finance', 'manager'],
    examples: ['Como está o orçamento deste mês frente ao realizado?'],
  },
  {
    id: 'recebiveis',
    label: 'Contas a receber',
    roles: ['finance'],
    examples: ['Quais cobranças estão em atraso?'],
  },
  {
    id: 'fiscal',
    label: 'Documentos fiscais',
    roles: ['finance', 'accountant'],
    examples: ['Quais notas fiscais estão aguardando autorização?'],
  },
  {
    id: 'contabilidade',
    label: 'Documentos para a contabilidade',
    roles: ['accountant'],
    examples: ['Quais documentos foram liberados para a contabilidade?'],
  },
  {
    id: 'reembolsos',
    label: 'Reembolso de despesas',
    roles: ['agent', 'manager', 'finance', 'inventory', 'sales', 'sales_inventory', 'technician', 'delivery'],
    examples: ['Paguei um pedágio de R$ 18 do meu bolso; quero pedir o reembolso de despesa.'],
  },
  {
    id: 'escalas',
    label: 'Escalas e trocas de turno',
    roles: EVERYONE,
    examples: ['Qual é a minha escala desta semana?'],
  },
  {
    id: 'horas',
    label: 'Horas trabalhadas e cobrança de horas',
    roles: EVERYONE.filter((role) => role !== 'agent'),
    examples: ['Registre 2 horas que trabalhei hoje no projeto da cliente.'],
  },
  {
    id: 'crm',
    label: 'Clientes, oportunidades e acompanhamentos',
    roles: ['agent', 'manager', 'sales', 'sales_inventory'],
    examples: ['Cadastre a cliente Ana Souza com o telefone dela.'],
  },
  {
    id: 'prontuario',
    label: 'Prontuário e material do atendimento',
    // crm.prontuario-write = rota /clinical-records; acesso clínico e recorte por paciente conferidos na ferramenta.
    roles: ['technician'],
    examples: ['Abra o rascunho da evolução da Ana: paciente evoluiu bem, sem dor.'],
  },
  {
    id: 'agenda',
    label: 'Agenda: faltas, confirmações, fila de chegada e novo atendimento',
    // agenda.read / agenda.write = CRM_AGENDA_ROLES; na clínica soma-se o acesso clínico individual (conferido na ferramenta).
    roles: ['manager', 'sales', 'sales_inventory', 'agent'],
    examples: ['Quais agendamentos aguardam confirmação esta semana?'],
  },
  {
    id: 'entregas',
    label: 'Entregas de vendas: o que falta entregar e a situação de cada entrega',
    roles: ['agent', 'manager', 'sales', 'sales_inventory', 'technician', 'delivery'],
    examples: ['O que falta entregar hoje?', 'Entreguei o pedido da Maria.'],
  },
  {
    id: 'vendas-pdv',
    label: 'Vender no PDV e conferir as próprias vendas',
    roles: ['agent', 'manager', 'sales', 'sales_inventory'],
    examples: [
      'Registre a venda de um vestido tamanho M, pago em Pix.',
      'Tem o vestido azul tamanho M? Quanto custa?',
      'Quais foram as minhas vendas de hoje?',
      'Abre a mesa 3 e lança dois cafés.',
    ],
  },
  {
    id: 'catalogo-consulta',
    label: 'Consultar produto, variação e estoque para vender',
    // ML185n: também quem vende pelo balcão (balcao.read), pelos comandos balcao.list/lookup/availability, sem custo.
    roles: ['agent', 'manager', 'sales', 'sales_inventory'],
    examples: ['Tem o vestido azul tamanho M? Quanto custa e quantos há no estoque?'],
  },
  {
    id: 'encomendas-balcao',
    label: 'Encomendas e ajustes no balcão: abrir para o cliente, receber a peça e acompanhar',
    // ML189a: encomendas.counter (orçamento, entrada da peça, consulta das abertas e das próprias, aviso de pronta), sem custo nem margem.
    roles: ['agent', 'manager', 'sales', 'sales_inventory'],
    examples: [
      'Abra um ajuste de barra para a cliente Ana Souza, entrega sexta.',
      'Quais encomendas minhas ainda estão abertas?',
    ],
  },
  {
    id: 'indicadores-vendas',
    label: 'Relatórios de vendas por período e origem',
    // ML185l: os comandos indicadores.period-sales/channels só aceitam owner/admin (a tela do atendimento só vê widgets públicos).
    roles: [],
    examples: ['Quanto vendemos esta semana por origem?'],
  },
  {
    id: 'patrimonio',
    label: 'Patrimônio e manutenção',
    // patrimonio.operate (bens sob a própria custódia) inclui estes papéis; ordens e planos são só do técnico (maintain).
    roles: ['manager', 'sales', 'inventory', 'sales_inventory', 'technician', 'delivery'],
    examples: ['Quais bens estão sob a minha custódia?'],
  },
  {
    id: 'contratos',
    label: 'Contratos e assinaturas',
    // contratos.read (consultas); preparar e aprovar são do gerente e de vendas; contratos técnicos, do financeiro.
    roles: ['manager', 'sales', 'finance'],
    examples: ['Quais contratos estão aguardando aprovação?'],
  },
  {
    id: 'loja',
    label: 'Loja online',
    roles: ['editor'],
    examples: ['Mostre o rascunho atual da loja.'],
  },
  {
    id: 'campanhas',
    label: 'Campanhas',
    roles: ['editor'],
    examples: ['Quais campanhas estão agendadas?'],
  },
];

/** As áreas visíveis para o papel; owner/admin recebem todas. */
export function rimatiChatAreasFor(role: string | undefined): RimatiChatArea[] {
  if (!role || !(USER_ROLES as readonly string[]).includes(role)) return [];
  if (role === 'owner' || role === 'admin') return [...RIMATI_CHAT_AREAS];
  return RIMATI_CHAT_AREAS.filter((area) => (area.roles as readonly string[]).includes(role));
}

/** Toda pessoa ativa tem ao menos as áreas comuns; a decisão por ferramenta fica no servidor. */
export function canOpenRimatiChat(role: string | undefined): boolean {
  return rimatiChatAreasFor(role).length > 0;
}
